import {
  type FC,
  type ChangeEvent,
  type FocusEvent,
  type MouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react';
import { optionalProp } from '../../core';
import DropDown from '../../DropDown/DropDown';
import { MLPVersion } from '../../Input/Input';
import { type PhoneNumberMLPProps } from '../types/propsType';
import { TooltipPreference } from '../../Tooltip/Tooltip';
import { type Data } from '../../DropDown/types/propsType';
import type { DropDownValidations } from '../../DropDown/types/enums';
import { DropDownFilterBy } from '../../DropDown/types/enums';
import styles from '../PhoneNumber.module.scss';

/**
 *
 *  by default : disabledFilter, dropdownDisabled, inputDisabled is false;
 *  name is required prop
 */

const PhoneNumberMLP1: FC<PhoneNumberMLPProps> = ({
  name,
  dropdownLabel = '',
  dropdownValue,
  dropdownLabelInfo = '',
  dropdownLabelInfoPreference = TooltipPreference.RIGHT,
  disabledFilter = false,
  dropdownDisabled,
  configData,
  inputValue = '',
  inputDisabled = false,
  onInputChange,
  status,
  onBlur,
  phoneCountryData,
  isElementFocused,
  isHovered,
  setIsElementFocused,
  setIsHovered,
  onMouseOver,
  onMouseLeave,
  onFocus,
  applySearchFilter,
  searchFilterPlaceHolder = 'Search',
  searchFilterValue,
  setSearchFilterValue,
  onClick,
  onChange,
  width,
  ...restProps
}): JSX.Element => {
  const handleMouseOver = (e: MouseEvent<HTMLInputElement>) => {
    if (!inputDisabled) {
      setIsHovered(true);
    }
    if (onMouseOver) {
      onMouseOver(e);
    }
  };

  const handleMouseLeave = (e: MouseEvent<HTMLInputElement>) => {
    setIsHovered(false);
    if (onMouseLeave) {
      onMouseLeave(e);
    }
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (!inputDisabled) {
      setIsElementFocused(true);
    }
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsElementFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleSearchQueryChange = (e: ChangeEvent<HTMLInputElement>) => {
    const regex = /^[A-Za-z]+$/; //Country name filter only allow alphabets
    if (!e.target.value || regex.test(e.target.value)) {
      if (setSearchFilterValue) {
        setSearchFilterValue(e.target.value);
      }
    }
  };

  return (
    <section
      className={`${styles['phone-number-body']} ${isHovered ? styles['element-ishovered'] : ''} ${
        isElementFocused ? styles['element-isfocused'] : ''
      }`}
    >
      <span
        className={`${styles['dropdown-number']} ${
          dropdownDisabled || inputDisabled ? styles['disable-dropdown'] : ''
        } ${status ? styles[status] : ''}`}
      >
        <DropDown
          mlpVersion={MLPVersion.ONE}
          testId="number-dropDown"
          disabled={dropdownDisabled || inputDisabled}
          name={name}
          value={inputValue}
          dropdownLabel={dropdownLabel}
          isPhoneNumberMLP1
          labelInfo={dropdownLabelInfo}
          labelInfoPreference={dropdownLabelInfoPreference}
          disabledFilter={disabledFilter}
          readOnly={inputDisabled}
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={(e) => {
            onInputChange?.(e.target.value, status, e);
            onChange?.(e);
          }}
          searchFilterPlaceHolder={searchFilterPlaceHolder}
          onChangeSearchQuery={handleSearchQueryChange}
          filterBy={DropDownFilterBy.STARTS_WITH}
          {...optionalProp('status', status as unknown as DropDownValidations)}
          {...optionalProp('onClick', onClick as (e?: ReactKeyboardEvent | ReactMouseEvent) => void)}
          {...optionalProp('configData', configData)}
          {...optionalProp('data', phoneCountryData)}
          {...optionalProp('width', width as string)}
          {...optionalProp('applySearchFilter', applySearchFilter)}
          {...optionalProp('searchFilterValue', searchFilterValue)}
          {...optionalProp(
            'selectedCountry',
            typeof dropdownValue === 'string'
              ? phoneCountryData?.find((country) => country.desc === dropdownValue)
              : (dropdownValue as Data),
          )}
          {...restProps}
        />
      </span>
    </section>
  );
};

export default PhoneNumberMLP1;
